import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Badge, Avatar } from '@/components';
import { Send, Lightbulb, Zap, Languages, Stethoscope, FileText, Users, BookOpen, HelpCircle, Loader2, AlertTriangle } from 'lucide-react';
import { useAgentChat } from '@/firebase/useAgentChat';
import { useSessionStore } from '@/stores/sessionStore';

const AIPatientAgent = () => {
  // TV-P7-11 — real Gemini chat via Cloud Function. Encounter id
  // comes from /ai-agent/:encounterId; fall back to sessionStore
  // for direct opens.
  const { encounterId: routeEncId } = useParams();
  const sessionEncId = useSessionStore((s) => s.currentEncounter);
  const encounterId = routeEncId || sessionEncId;
  const { messages, sendMessage, sending, error } = useAgentChat(encounterId);

  // Auto-scroll to bottom as messages stream in.
  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  // Legacy hardcoded demo messages removed (TV-P7-11). Old shape
  // kept only for diff archaeology; real conversation flows from
  // useAgentChat() above.
  /* const __legacy_messages = [
    {
      id: 1,
      sender: 'ai',
      text: "Hello Maria! I'm your AI health assistant. I'm here to help you understand your care plan, answer questions about your health, and provide support between visits.",
      timestamp: '10:30 AM',
    },
    {
      id: 2,
      sender: 'user',
      text: 'What are some things we should talk about?',
      timestamp: '10:31 AM',
    },
    {
      id: 3,
      sender: 'ai',
      text: 'Great question! Based on your visit today, here are some important topics we could discuss:',
      bullets: [
        'How to use your new Semaglutide injection and manage side effects',
        'Tips for building sustainable eating habits and meal planning',
        'Understanding your blood sugar numbers and what HbA1c really means',
      ],
      timestamp: '10:32 AM',
    },
    {
      id: 4,
      sender: 'user',
      text: 'What is my HbA1c and why does it matter?',
      timestamp: '10:33 AM',
    },
    {
      id: 5,
      sender: 'ai',
      text: 'Your HbA1c is 8.2%, which tells us how well your blood sugar has been controlled over the past 2-3 months. Think of it as an average—a higher number means your blood sugar has been running higher on average. Your goal is to get it below 7%, which reduces your risk of complications like eye and kidney problems. Right now, starting the Semaglutide should help bring it down.',
      timestamp: '10:34 AM',
    },
    {
      id: 6,
      sender: 'user',
      text: "I'm nervous about giving myself injections. Will it hurt?",
      timestamp: '10:35 AM',
    },
    {
      id: 7,
      sender: 'ai',
      text: "That's a very normal concern. Here's what to expect:",
      bullets: [
        'The needle is very thin and small—most people say it feels like a quick pinch',
        'You inject under the skin (not into a muscle), so it causes minimal discomfort',
        'Our nurse will show you exactly how to do it, and you can practice in the office first',
        'The more you do it, the easier it becomes. Many patients feel confident after 2-3 injections',
      ],
      timestamp: '10:36 AM',
    },
  ]; */ // end TV-P7-11 legacy archaeology block

  const [inputValue, setInputValue] = useState('');

  const capabilities = [
    {
      icon: Lightbulb,
      title: 'Smart Questions',
      description: 'Ask anything about your condition, medications, or care plan',
    },
    {
      icon: Zap,
      title: 'Term Translator',
      description: 'Confused by medical jargon? I explain it in plain English',
    },
    {
      icon: Languages,
      title: 'Multi-Language',
      description: 'Communicate in Spanish, Mandarin, Vietnamese, and more',
    },
    {
      icon: Stethoscope,
      title: 'Symptom Triage',
      description: 'Describe symptoms to get guidance on urgency',
    },
    {
      icon: FileText,
      title: 'Visit Summary',
      description: 'Get a plain-language recap of your visit',
    },
    {
      icon: Users,
      title: 'Medication Info',
      description: 'Learn about your medications and how to take them',
    },
    {
      icon: BookOpen,
      title: 'Care Plan Coach',
      description: 'Help staying on track with your treatment plan',
    },
    {
      icon: HelpCircle,
      title: 'Health Education',
      description: 'Learn about managing your health conditions',
    },
  ];

  // TV-P7-11 — real Gemini agent. sendMessage() calls the agentRespond
  // Cloud Function which persists BOTH the user turn and the model's
  // reply to Firestore. We don't optimistic-write; the snapshot
  // listener in useAgentChat picks them up as they land. Empty
  // `text` no-ops via the hook.
  const handleSendMessage = async () => {
    const text = inputValue.trim();
    if (!text || sending) return;
    setInputValue('');
    await sendMessage(text);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b0d14] to-[#0f1119] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">AI Patient Companion</h1>
          <p className="text-gray-400">Your personal health guide • Available 24/7</p>
        </div>

        {/* Main Content - Split Layout */}
        <div className="grid grid-cols-3 gap-6">
          {/* Left Column - Chat (65%) */}
          <div className="col-span-2">
            <Card className="p-6 h-screen max-h-[calc(100vh-200px)] flex flex-col">
              {/* Messages — TV-P7-11 reads from Firestore via useAgentChat */}
              <div ref={scrollRef} className="flex-grow overflow-y-auto mb-6 space-y-4">
                {error && (
                  <div className="text-xs text-red-400 p-3 bg-red-500/10 rounded-lg border border-red-500/30 flex gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>Chat error: {error.message}</span>
                  </div>
                )}
                {!error && messages.length === 0 && (
                  <div className="text-center text-sm text-gray-500 italic py-12">
                    Ask anything about your health — your AI companion is here to help.
                  </div>
                )}
                {messages.map((message) => {
                  const isAssistant = message.role === 'assistant';
                  const ts = message.createdAt?.toDate?.()
                    ?? (message.createdAt?.seconds
                      ? new Date(message.createdAt.seconds * 1000)
                      : null);
                  const tsLabel = ts
                    ? ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : '';
                  return (
                    <div
                      key={message.id}
                      className={`flex ${isAssistant ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`flex gap-3 max-w-md ${isAssistant ? 'flex-row' : 'flex-row-reverse'}`}
                      >
                        <div className="flex-shrink-0">
                          <div
                            className={`w-10 h-10 rounded-full border flex items-center justify-center ${
                              isAssistant
                                ? 'bg-purple-500/20 border-purple-500'
                                : 'bg-blue-500/20 border-blue-500'
                            }`}
                          >
                            <span className="text-lg">{isAssistant ? '🤖' : '👤'}</span>
                          </div>
                        </div>
                        <div className="flex flex-col">
                          <div
                            className={`px-4 py-3 rounded-lg ${
                              isAssistant
                                ? 'bg-purple-500/20 border border-purple-500/30 text-gray-100'
                                : 'bg-blue-500/20 border border-blue-500/30 text-gray-100'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                          </div>
                          {tsLabel && (
                            <span className="text-xs text-gray-500 mt-1 px-1">{tsLabel}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {sending && (
                  <div className="flex justify-start">
                    <div className="flex gap-3 max-w-md">
                      <div className="w-10 h-10 rounded-full bg-purple-500/20 border border-purple-500 flex items-center justify-center">
                        <span className="text-lg">🤖</span>
                      </div>
                      <div className="px-4 py-3 rounded-lg bg-purple-500/10 border border-purple-500/20 text-gray-300 flex items-center gap-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span className="text-xs italic">Thinking…</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Message Input */}
              <div className="pt-4 border-t border-[#1e2235]">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    disabled={sending}
                    placeholder="Ask me anything about your health..."
                    className="flex-grow px-4 py-3 bg-[#12141e] border border-[#1e2235] rounded-lg text-gray-100 placeholder-gray-600 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 disabled:opacity-50"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={sending || !inputValue.trim()}
                    className="px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                  >
                    {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </Card>
          </div>

          {/* Right Column - Capabilities Sidebar (35%) */}
          <div>
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-white mb-1">My Capabilities</h3>
              <p className="text-sm text-gray-400 mb-6">What I can help with</p>

              <div className="space-y-3">
                {capabilities.map((capability, idx) => {
                  const IconComponent = capability.icon;
                  return (
                    <div
                      key={idx}
                      className="p-4 bg-[#12141e] rounded-lg border border-[#1e2235] hover:border-purple-500/50 hover:bg-[#1e2235] transition-all cursor-pointer group"
                    >
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 text-purple-400 group-hover:text-purple-300">
                          <IconComponent className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm text-gray-100 group-hover:text-white">
                            {capability.title}
                          </p>
                          <p className="text-xs text-gray-500 mt-1 leading-tight">
                            {capability.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Info Box */}
              <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-xs text-gray-300 leading-relaxed">
                  💡 <span className="font-semibold">Tip:</span> I'm available 24/7 but not a substitute for emergency care. Always call 911 for emergencies.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIPatientAgent;
